import { computed, DestroyRef, inject, Service, signal } from '@angular/core';

import { ActionState } from '@shared/state/async-action';
import { PlatformService } from '@core/platform/platform.service';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { OrganizationsRepository } from '../domain/interfaces/organizations.interface';
import {
  EMPTY_ORG_WHEEL,
  OrgWheel,
  OrgWheelAnswers,
  OrgWheelDimension,
  checkinLanded,
  retainKnownAnswers,
  wheelCheckinOrder,
  wheelProgressPercent,
} from '../domain/models/org-wheel.model';
import { OrgError } from '../domain/org.errors';
import { OrgErrorMapper } from './org-error.mapper';
import { destroyNotifier, firstValueFromScoped, runOrgAction } from './org-action';

/** The check-in walk: idle → answering one dimension at a time → summary. */
export type OrgWheelMode = 'idle' | 'checking' | 'summary';

/**
 * OrgWheelFacade — «عجلة الأبعاد» (§3).
 *
 * The wheel is a SELF-ASSESSMENT: the member's answers ARE the scores, so this
 * facade computes no score of its own. It owns the check-in walk (which
 * dimension we are on, what has been answered so far) and hands the finished map
 * to the server, which answers with THE ENTIRE WHEEL — re-rendered directly,
 * never followed by a GET (§3.2).
 *
 * Two traps shape the submit path and are the reason it is longer than a
 * one-line POST:
 *
 *  · Trap 7 — unknown keys are dropped SILENTLY, with a 200. So the draft is
 *    filtered to keys from the wheel we just rendered BEFORE sending, and the
 *    response is then checked to confirm something actually landed.
 *  · Trap 6 — `total` is what this org shows, not always 9. Progress is rendered
 *    `answered_today / total` from the payload, never against a hardcoded count.
 */
@Service({ autoProvided: false })
export class OrgWheelFacade {
  private readonly repo = inject(OrganizationsRepository);
  private readonly errors = inject(OrgErrorMapper);
  private readonly platform = inject(PlatformService);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('OrgWheelFacade');
  private readonly destroyed = destroyNotifier(inject(DestroyRef));

  private readonly _wheel = signal<OrgWheel>(EMPTY_ORG_WHEEL);
  private readonly _loadError = signal<OrgError | null>(null);
  private readonly _submitError = signal<OrgError | null>(null);
  private readonly _loadedAt = signal(0);

  private readonly _mode = signal<OrgWheelMode>('idle');
  private readonly _step = signal(0);
  private readonly _draft = signal<OrgWheelAnswers>({});
  /**
   * Set when a 200 came back having kept fewer answers than we sent — the one
   * case where the transport succeeded and the write still did not happen.
   */
  private readonly _dropped = signal(false);

  readonly load = new ActionState('reference_portal_org_wheel_failed', 'GET /org/wheel');
  readonly submit = new ActionState('reference_portal_org_wheel_checkin_failed', 'POST /org/wheel/check-in');

  readonly wheel = this._wheel.asReadonly();
  readonly loadError = this._loadError.asReadonly();
  readonly submitError = this._submitError.asReadonly();
  readonly mode = this._mode.asReadonly();
  readonly step = this._step.asReadonly();
  readonly draft = this._draft.asReadonly();
  readonly answersDropped = this._dropped.asReadonly();

  /** First paint has no data yet — the view shows its skeleton, not an empty wheel. */
  readonly isFirstLoad = computed(() => this._loadedAt() === 0 && !this.load.failed());

  /** Core first, then the org's own order. The sequence the check-in walks. */
  readonly checkinOrder = computed(() => wheelCheckinOrder(this._wheel()));
  readonly totalSteps = computed(() => this.checkinOrder().length);
  readonly currentDimension = computed<OrgWheelDimension | null>(
    () => this.checkinOrder()[this._step()] ?? null,
  );

  /** `answered_today / total` from the payload — trap 6, never against a hardcoded 9. */
  readonly progressPercent = computed(() => wheelProgressPercent(this._wheel()));
  readonly answeredToday = computed(() => this._wheel().answeredToday);
  readonly total = computed(() => this._wheel().total);
  readonly composite = computed(() => this._wheel().composite);
  readonly streak = computed(() => this._wheel().streak);
  readonly checkedInToday = computed(() => this._wheel().checkedInToday);
  /** The four options, in order, FROM the payload (§3.1) — nothing is hardcoded. */
  readonly scale = computed(() => this._wheel().scale);

  /**
   * The urgent-session bridge: true when the core dimension's score is ≤ 25.
   * A floor check, not a diagnosis — and read straight from the server so the
   * threshold lives in one place.
   */
  readonly crisis = computed(() => this._wheel().crisis);

  /** Has the member answered anything, ever? Drives "start" vs "retake" copy. */
  readonly hasAnyScore = computed(() => this.checkinOrder().some((d) => d.score != null));

  // ── reads ───────────────────────────────────────────────────────────────
  /**
   * `GET /org/wheel`. Works for a NON-MEMBER too (§3.1 — the shared catalogue
   * with no org customisation), so this deliberately does not gate on context.
   */
  async ensureLoaded(): Promise<void> {
    if (!this.platform.isBrowser || this._loadedAt() > 0 || this.load.loading()) return;
    await this.reload();
  }

  async reload(): Promise<void> {
    const wheel = await runOrgAction(this.load, this._loadError, this.errors, () =>
      firstValueFromScoped(this.repo.getWheel(), this.destroyed),
    );
    if (wheel) {
      this._wheel.set(wheel);
      this._loadedAt.set(Date.now());
    }
  }

  // ── the check-in walk ───────────────────────────────────────────────────
  /** Begin a fresh check-in. An amendment is normal: a second submit updates the same day. */
  start(): void {
    this._draft.set({});
    this._step.set(0);
    this._dropped.set(false);
    this._submitError.set(null);
    this._mode.set('checking');
  }

  cancel(): void {
    this._mode.set('idle');
  }

  back(): void {
    if (this._step() > 0) this._step.set(this._step() - 1);
    else this._mode.set('idle');
  }

  /**
   * Answer the current step. Advances, or — on the last one — submits.
   *
   * A PARTIAL map is normal and expected (§3.2): answering four dimensions now
   * and five tonight produces ONE check-in with nine answers, because the unique
   * key is (user, day). So {@link finish} is also callable at any point.
   */
  async answer(value: number): Promise<void> {
    const dim = this.currentDimension();
    if (!dim) return;
    this._draft.set({ ...this._draft(), [dim.key]: value });
    if (this._step() < this.totalSteps() - 1) {
      this._step.set(this._step() + 1);
      return;
    }
    await this.finish();
  }

  /** Submit whatever has been answered so far — a partial map is a first-class case. */
  async finish(): Promise<void> {
    const before = this._wheel();
    // Trap 7: only ever POST keys from the wheel we just rendered, and only the
    // four values the server accepts. Anything else is dropped silently with a
    // 200, which would look exactly like success.
    const answers = retainKnownAnswers(before, this._draft());
    const sentCount = Object.keys(answers).length;
    if (sentCount === 0) {
      this._mode.set('idle');
      return;
    }

    const after = await runOrgAction(this.submit, this._submitError, this.errors, () =>
      firstValueFromScoped(this.repo.submitCheckin(answers), this.destroyed),
    );
    if (!after) return; // the error state is already set; the view offers a retry

    // The response IS the whole wheel — re-render from it, no follow-up GET.
    this._wheel.set(after);
    this._loadedAt.set(Date.now());

    // Trap 7, second half: a 200 does not prove the answers landed. Verify
    // against `answered_today` / the per-dimension scores before telling the
    // member their check-in was saved.
    const landed = checkinLanded(before, after, sentCount);
    this._dropped.set(!landed);
    if (!landed) {
      this.logger.warn('Check-in returned 200 but no answer landed', { sent: Object.keys(answers) });
    }
    this._mode.set('summary');
  }

  /** Leave the summary and return to the wheel. */
  done(): void {
    this._mode.set('idle');
    this._dropped.set(false);
  }

  /** Dismiss an inline submit error without leaving the walk. */
  clearSubmitError(): void {
    this._submitError.set(null);
    this.submit.reset();
  }
}
